import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { isScheduleMobileWorkingLocation } from "../../../components/Schedule/schedule-event-location.component";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import {
  classifyDashboard2BookingBucket,
  selectPrimaryBookingForDay,
  sortBookingsByStart
} from "./dashboard2-primary-booking.utils";

export type ScheduleBookingDetails = {
  inventoryName: string;
  locationName?: string;
  floorName?: string;
};

export function scheduleStartDayKey(schedule: BookingScheduleInterface): string {
  const zone = schedule.timezone ?? DateTime.now().zoneName;
  return DateTime.fromISO(schedule.startDate).setZone(zone).toFormat("yyyy-MM-dd");
}

export function schedulesForUserOnDay(
  schedules: BookingScheduleInterface[],
  userId: string,
  day: DateTime
): BookingScheduleInterface[] {
  const dayKey = day.toFormat("yyyy-MM-dd");

  // Status owner is userBookedFor (occupant). userBookedBy may differ when someone
  // else creates the booking; it must not move the row into the booker's day status.
  return schedules.filter(schedule => {
    if (schedule.userBookedFor !== userId) {
      return false;
    }

    return scheduleStartDayKey(schedule) === dayKey;
  });
}

export function schedulesForDay(
  schedules: BookingScheduleInterface[],
  day: DateTime
): BookingScheduleInterface[] {
  const dayKey = day.toFormat("yyyy-MM-dd");

  return schedules.filter(schedule => scheduleStartDayKey(schedule) === dayKey);
}

export function schedulesForUserToday(
  schedules: BookingScheduleInterface[],
  userId: string
): BookingScheduleInterface[] {
  const todayKey = DateTime.now().toFormat("yyyy-MM-dd");

  return schedules.filter(
    schedule => schedule.userBookedFor === userId && scheduleStartDayKey(schedule) === todayKey
  );
}

export function selectPrimaryBookingForUserOnDay(
  schedules: BookingScheduleInterface[],
  userId: string,
  day: DateTime
): BookingScheduleInterface | undefined {
  const dayBookings = schedulesForUserOnDay(schedules, userId, day);
  const isToday = day.toFormat("yyyy-MM-dd") === DateTime.now().toFormat("yyyy-MM-dd");
  const { primary } = selectPrimaryBookingForDay(dayBookings, { isToday });

  return primary;
}

export function selectPrimaryTodayBookingForUser(
  schedules: BookingScheduleInterface[],
  userId: string
): BookingScheduleInterface | undefined {
  return selectPrimaryBookingForUserOnDay(schedules, userId, DateTime.now());
}

export function selectPrimaryWorkplaceBookingForUserOnDay(
  schedules: BookingScheduleInterface[],
  userId: string,
  day: DateTime
): BookingScheduleInterface | undefined {
  const dayBookings = schedulesForUserOnDay(schedules, userId, day);
  const ownWorkplaces = sortBookingsByStart(
    dayBookings.filter(booking => classifyDashboard2BookingBucket(booking) === "workplace")
  );

  return ownWorkplaces[0];
}

export function findScheduleById(
  schedules: BookingScheduleInterface[],
  bookingId: string | number
): BookingScheduleInterface | undefined {
  const id = typeof bookingId === "string" ? Number.parseInt(bookingId, 10) : bookingId;

  return schedules.find(schedule => schedule.id === id);
}

/** Default 24-hour time token; overridden per locale (e.g. `hh:mm a` for English). */
export const DASHBOARD2_DEFAULT_TIME_FORMAT = "HH:mm";

export function formatScheduleTimeRange(
  schedule: BookingScheduleInterface,
  timeFormat: string = DASHBOARD2_DEFAULT_TIME_FORMAT
): string {
  const start = DateTime.fromISO(schedule.startDate).toFormat(timeFormat);
  const end = DateTime.fromISO(schedule.endDate).toFormat(timeFormat);
  return `${start}–${end}`;
}

export function formatScheduleBookingChipLabels(
  details: ScheduleBookingDetails,
  timeLabel: string
): { primaryLabel: string; secondaryLabel: string; tooltip: string } {
  const showFloorName = !!details.floorName && details.floorName !== details.locationName;
  const placeParts = [details.locationName, showFloorName ? details.floorName : undefined].filter(
    Boolean
  );
  const placeLabel = placeParts.join(" · ");
  const hasInventoryName = !!details.inventoryName;
  const primaryLabel =
    details.inventoryName || details.locationName || details.floorName || timeLabel;

  let secondaryPlaceLabel = "";
  if (hasInventoryName) {
    secondaryPlaceLabel = placeLabel;
  } else if (showFloorName) {
    secondaryPlaceLabel = details.floorName ?? "";
  }

  const secondaryLabel = [secondaryPlaceLabel, timeLabel].filter(Boolean).join(" · ");
  const tooltip = [primaryLabel, placeLabel, timeLabel].filter(Boolean).join(" · ");

  return { primaryLabel, secondaryLabel, tooltip };
}

export function resolveScheduleBookingDetails(
  schedule: BookingScheduleInterface,
  mobileWorkingLabel: string
): ScheduleBookingDetails {
  if (isScheduleMobileWorkingLocation(schedule)) {
    return { inventoryName: mobileWorkingLabel };
  }

  const inventoryName = schedule.bookingInventory?.name?.trim() ?? "";
  const locationName = schedule.locationName?.trim() || undefined;
  const floorName = schedule.floorName?.trim() || undefined;

  return { inventoryName, locationName, floorName };
}

/** Accessible label for opening the booked place or room on the floor plan. */
export function scheduleFloorPlanActionLabel(
  schedule: BookingScheduleInterface,
  t: TFunction
): string {
  return schedule.bookingType === BookingType.CONFERENCEZONE ? t("Show Zone") : t("Show place");
}
